import { useEffect, useState, useCallback, useMemo } from "react";
import { IHotel, INonFlyingAssignment } from "@vmosolutions/oc-interfaces";
import { Grid, TableCell, TableRow } from "@mui/material";
import { convertMilli2DaysHrsMin } from "src/utils/index";
import { SelectField } from "src/components/FormikComponents/SelectField";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import _ from "lodash";
import { onDutyRestHotelIsDirty } from "../PairingEditorWindow";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface INFAHotelSelector {
  id: string;
  nfa: INonFlyingAssignment;
  updateEventGroups: any;
}

export const NFAHotelSelector = (props: INFAHotelSelector): JSX.Element => {
  const { nfa, updateEventGroups } = props;
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[props.id]);
  const [hotelOptions, setHotelOptions] = useState<IHotel[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<IHotel>(undefined);

  const nfaDirtyHotels = useMemo(() => onDutyRestHotelIsDirty(storedPairing), [storedPairing]);

  /** Sort hotels dropdown list by priority */
  const sortHotelsByPriority = (a: IHotel, b: IHotel): number => {
    return b.priority > a.priority ? -1 : 1;
  };

  useEffect(() => {
    const getHotelOptions = async (): Promise<void> => {
      const newHotelOptions = await AmplifyAPIWrapper.Get<IHotel[]>(`/hotels?icao=${nfa?.locationICAO}`);
      setHotelOptions(
        Array.isArray(newHotelOptions) && newHotelOptions.length > 0 ? newHotelOptions.sort(sortHotelsByPriority) : []
      );
    };

    if (nfa?.locationICAO) getHotelOptions();
  }, [nfa]);

  const initialHotel = useMemo(() => {
    return hotelOptions.find((hotel) => hotel._id === nfa?.custom?.hotel_id);
  }, [hotelOptions, nfa]);

  //set hotel...
  useEffect(() => {
    setSelectedHotel(initialHotel);
  }, [initialHotel]);

  //I should show to user whatever hotel is there, even if it isn't from that location of the NFA
  useEffect(() => {
    const getHotelById = async (): Promise<void> => {
      const hotel = await AmplifyAPIWrapper.Get<IHotel>(`/hotel/${nfa?.custom?.hotel_id}`);
      setSelectedHotel(hotel);
    };

    if (hotelOptions.length && !initialHotel && nfa?.custom?.hotel_id) getHotelById();
  }, [hotelOptions.length, initialHotel, nfa]);

  const hotelsDirty = useMemo(() => nfaDirtyHotels.includes(nfa?._id), [nfa, nfaDirtyHotels]);

  const handleUpdateNFAHotel = useCallback(
    (updatedEvent: INonFlyingAssignment): void => {
      const eventGroups = _.cloneDeep(storedPairing?.eventGroups);

      let itemIndex = -1;
      for (const group of eventGroups) {
        itemIndex = group.nonFlyingAssignments.findIndex((currentEvent: INonFlyingAssignment) =>
          //check if it is an unsaved NFA, if it is check on the tempId, else check on the object id
          updatedEvent._tempId ? currentEvent._tempId === updatedEvent._tempId : currentEvent._id === updatedEvent._id
        );
        // If found, edit it, and break out of for loop.
        if (itemIndex !== -1) {
          group.nonFlyingAssignments[itemIndex] = updatedEvent;
          // Runs changes through draft pairing endpoint and updates form fields accordingly.
          updateEventGroups(eventGroups, storedPairing);
        }
      }
    },
    [storedPairing]
  );

  const handleOnChange = useCallback(
    (hotel: IHotel) => {
      setSelectedHotel(hotel);
      handleUpdateNFAHotel({ ...nfa, ...{ custom: { hotel_id: hotel?._id } } });
    },
    [handleUpdateNFAHotel, nfa]
  );
  if (!nfa) {
    return null;
  }
  //organize the display string for duty
  return (
    <TableRow>
      <TableCell colSpan={5}>
        <Grid className={`rest_period_summary on_duty_rest ${hotelsDirty && "dirty"}`}>
          <Grid item style={{ fontWeight: 500, marginRight: 5 }}>
            On Duty Rest
          </Grid>
          <Grid item className="rest_duration" style={{ marginRight: 5 }}>
            {`${convertMilli2DaysHrsMin(nfa?.end?.valueOf() - nfa?.start?.valueOf())} `}
          </Grid>
          At
          <SelectField
            className={`rest_hotel_selector ${hotelsDirty && "dirty"}`}
            label=""
            name={"rest_hotel"}
            getOptionLabel={(option: IHotel) =>
              `${option?.name} ${option?.phoneNumber ? ` (${option?.phoneNumber})` : ""}`
            }
            getOptionValue={(option: IHotel) => option?._id}
            closeMenuOnSelect={true}
            hideSelectedOptions={false}
            options={hotelOptions}
            onChange={handleOnChange}
            value={selectedHotel}
            isClearable={true}
            placeholder="Select Hotel..."
            menuPosition="fixed"
            maxMenuHeight={400}
          />
          <Grid item className="rest_origin">{`in ${nfa?.locationICAO}`}</Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};
